import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

enum ACTIONS {
  OPEN_MODAL = "OPEN_MODAL",
  CLOSE_MODAL = "CLOSE_MODAL",
}

interface ModalState {
  isContactModalOpen: boolean;
}

interface ModalContextValue extends ModalState {
  handleChangeModalVisibility: (visibilityStatus: boolean) => void;
}

const initialState: ModalState = { isContactModalOpen: false };

const modalReducer = (
  state: ModalState,
  action: { type: ACTIONS }
): ModalState => {
  switch (action.type) {
    case ACTIONS.OPEN_MODAL:
      return { ...state, isContactModalOpen: true };
    case ACTIONS.CLOSE_MODAL:
      return { ...state, isContactModalOpen: false };
    default:
      return state;
  }
};
const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const ModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(modalReducer, initialState);

  const handleChangeModalVisibility = useCallback(
    (visibilityStatus: boolean) =>
      dispatch({
        type: visibilityStatus ? ACTIONS.OPEN_MODAL : ACTIONS.CLOSE_MODAL,
      }),
    []
  );

  const { isContactModalOpen } = useMemo(() => state, [state]);

  return (
    <ModalContext.Provider
      value={{ isContactModalOpen, handleChangeModalVisibility }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = (): ModalContextValue => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
